import React, { FC, useState, useEffect, useId } from 'react';
import DeleteTaskDialog from './DeleteTaskDialog';
import AddNewTaskDialog from './AddNewTaskDialog';
import axiosInstance from 'config/axiosInstance';
import { taskCandidatePage } from 'components/Client/Utility/candidates';
import { useRemoteSearch } from 'reusable/RemoteSearch/useRemoteSearch';
import RemoteListFooter from 'reusable/RemoteSearch/RemoteListFooter';
import { RemotePage } from 'reusable/RemoteSearch/remotePage';
import { useTranslation } from 'react-i18next';
import i18n from 'config/i18n';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';
import TaskSlotGrid, { TASK_SLOTS } from 'reusable/Client/TaskSlotGrid';
import { secondsToHMS, totalSeconds } from 'reusable/Client/totalSeconds';
import { durationText } from 'reusable/durationText';

interface Task {
  id: string;
  is_active: boolean;
  name: string;
  icon: string;
  value: string;
  client_delivery_count: string;
  total_hours_spent: string;
}

interface ManageTaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

// ADM-16。単位は表示の言語で出す（以前は「2 hrs 5 mins」の英語の固定文字）。
// EV-299。配送先から作業を外す確認と同じ時・分・秒（既存の文言）で出す。以前は分を切り上げていた。
function convertSeconds(seconds: number): string {
  // EV-312。値が数でないと英語の入れ子の文言が崩れるため、共通の関数で「-」にする。
  return durationText((key, options) => i18n.t(key, options), 'remove_task.duration_value', { ...secondsToHMS(seconds) }, { ns: 'client' });
}

const ManageTaskDialog: FC<ManageTaskDialogProps> = ({
  isOpen,
  onClose,
  title,
}) => {
  const { t } = useTranslation('client');
  const [isTaskRemovalDialogOpen, setIsTaskRemovalDialogOpen] = useState(false);
  const [isAddNewTaskDialogOpen, setIsAddNewTaskDialogOpen] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState<Task | null>(null);

  const beginSummary = useLatestRequest();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFailure, setSummaryFailure] = useState<{ task: Task; error: unknown } | null>(null);

  // EV-493。作業の一覧は全件（最大5,000件・50回）を読まず、20件ずつ読む（続きは「もっと見る」）。件数（上限の
  // 15件の表示）はサーバーの件数で決める。開いたとき・中のダイアログ（削除の確認・新規作成）を閉じたときに読み直す。
  // EV-151。失敗したら前回の一覧を残さない（useRemoteSearch）。同じ名前の確認は新規作成のダイアログがサーバーに聞く。
  const listOpen = isOpen && !isTaskRemovalDialogOpen && !isAddNewTaskDialogOpen;
  const taskList = useRemoteSearch<Task>({
    fetchPage: (_query, page) => taskCandidatePage('', page) as unknown as Promise<RemotePage<Task>>,
    getKey: (task) => task.id,
    enabled: listOpen,
  });
  const tasks = taskList.status === 'failed' ? undefined : taskList.items;
  const tasksCount = taskList.total ?? taskList.items.length;
  const loading = taskList.status === 'loading' || (listOpen && taskList.status === 'idle');
  const fetchError = taskList.status === 'failed' ? taskList.error : null;
  const fetchData = taskList.reload;

  useEffect(() => {
    if (isOpen) {
      setIsTaskRemovalDialogOpen(false); setIsAddNewTaskDialogOpen(false);
      setSummaryFailure(null); setSummaryLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => { beginSummary(); };
  }, [isOpen, isTaskRemovalDialogOpen, isAddNewTaskDialogOpen, beginSummary]);

  const handleTaskRemovalClick = async (task: Task) => {
    const isCurrent = beginSummary();
    setSummaryLoading(true); setSummaryFailure(null);
    try {
      const response = await axiosInstance.get(`/v1/tasks/${task.id}/summarize/`);
      if (!isCurrent()) return;
      // EV-288。数値の秒（新しい API）も、秒の文字列・null（古い API）も同じ秒として読む。
      setSelectedTaskData({ ...response.data, total_hours_spent: convertSeconds(totalSeconds(response.data.total_hours_spent)) });
      setIsTaskRemovalDialogOpen(true);
    } catch (error) {
      if (isCurrent()) setSummaryFailure({ task, error });
    } finally {
      if (isCurrent()) setSummaryLoading(false);
    }
  };

  const handleTaskRemovalDialogClose = () => {
    setIsTaskRemovalDialogOpen(false);
  };

  const handleAddNewTaskClick = () => {
    setIsAddNewTaskDialogOpen(true);
  };

  const handleAddNewTaskDialogClose = () => {
    setIsAddNewTaskDialogOpen(false);
  };

  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <>
      {/* EV-117。中のダイアログを開く間も外側は外さずに隠す（外して作り直すと、フォーカスの
          戻り先（開いたボタン）が無くなり、作り直した要素に Escape・Tab の処理も付かなかった）。 */}
      {inModalLayer(
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          hidden={isTaskRemovalDialogOpen || isAddNewTaskDialogOpen}
          aria-hidden={isTaskRemovalDialogOpen || isAddNewTaskDialogOpen || undefined}
        >
          <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div
              className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[880px]"
              role="dialog"
              ref={dialogRef}
              aria-modal="true"
              aria-labelledby={headingId}
              tabIndex={-1}
            >
              <div className="bg-white">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left sm:mt-0 w-full">
                    <div className="flex justify-between items-center mt-4 mx-6">
                      <h3
                        className="text-subtitle-1 font-medium text-neutral/300"
                        id={headingId}
                      >
                        {title}
                      </h3>
                      <button type="button"
                        onClick={onClose}
                        className="text-gray-600 hover:text-gray-800 focus:outline-none"
                      >
                        <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <hr className="border-gray-300 mt-4 w-full" />
                    <form>
                      {(loading || summaryLoading) && <p role="status">{t('ui.loading', { ns: 'common' })}</p>}
                      {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
                      {summaryFailure ? <FetchFailed error={summaryFailure.error} onRetry={() => handleTaskRemovalClick(summaryFailure.task)} message={t('ui.detail-fetch-failed', { ns: 'common' })} /> : null}
                      {/* A27（2026-11-03 和田様の指摘）。3行×5列の15枠。空の枠を押して追加、枠の右上の「×」で削除。
                          以前は上の「タスク作成」と、下の「タスク削除」で切り替える削除のモードだった。 */}
                      <div className="p-6">
                        {!fetchError && tasks ? (
                          <TaskSlotGrid
                            tasks={tasks}
                            onAdd={tasksCount < TASK_SLOTS ? handleAddNewTaskClick : undefined}
                            addLabel={t('manage_task.create_new_task')}
                            onRemove={(task) => handleTaskRemovalClick(task)}
                            removeLabel={(task) => `${task.name} ${t('ui.delete', { ns: 'common' })}`}
                            disabled={loading || summaryLoading}
                          />
                        ) : null}
                        {!loading && !fetchError && tasksCount >= TASK_SLOTS ? (
                          <p className="mt-4 text-center text-neutral/100 text-subtitle-2">
                            {t('manage_task.max_task')}
                          </p>
                        ) : null}
                      </div>
                      {/* EV-493。続き（もっと見る）と、続きを読めなかったときの知らせ。1ページ目は上の表示のまま。 */}
                      <div className="px-6 pb-4">
                        <RemoteListFooter search={taskList} showStatus={false} />
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTaskRemovalDialogOpen && selectedTaskData && (
        <DeleteTaskDialog
          taskName={selectedTaskData.name}
          isOpen={isTaskRemovalDialogOpen}
          onClose={handleTaskRemovalDialogClose}
          fields={[
            {
              label: t('delete_task.task_name'),
              value: selectedTaskData.name,
            },
            {
              label: t('delete_task.total_client'),
              value: selectedTaskData.client_delivery_count || '0',
            },
            {
              label: t('delete_task.total_duration'),
              value: selectedTaskData.total_hours_spent || '0',
            },
          ]}
          taskId={selectedTaskData.id}
        />
      )}

      {isAddNewTaskDialogOpen && (
        <AddNewTaskDialog
          isOpen={isAddNewTaskDialogOpen}
          onClose={handleAddNewTaskDialogClose}
          onReload={fetchData}
        />
      )}
    </>
  );
};

export default ManageTaskDialog;

export const importAllIcons = async () => {
  const iconNames = [
    'leave_for_work',
    'prepare_departure',
    'driving',
    'cargo_bulk',
    'cargo_pallet',
    'exchange_cargo',
    'exchange_cargo_pl',
    'wait_for_cargo',
    'unload_bulk',
    'unload_pallet',
    'rest',
    'wait_for_price_reduction',
    'prepare_to_go_home',
    'return_home',
    'walking',
    'banknotes',
    'battery_50',
    'briefcase',
    'clipboard_document_check',
    'clipboard_document_list',
    'document_check',
    'exclamation_triangle',
    'face_frown',
    'face_smile',
    'finger_print',
    'key',
    'moon',
    'photo',
    'star',
    'truck',
    'user_group',
    'wrench_screwdriver',
    'clock',
    'credit_card',
  ];

  const iconsMap: { [key: string]: string } = {};
  await Promise.all(
    iconNames.map(async (name) => {
      const icon = await import(`../../../assets/icons/tasks/${name}.svg`);
      iconsMap[name] = icon.default;
    })
  );

  return iconsMap;
};
